import { FileUrlVariant, PublicUrlAdapter } from '@magicdoorinc/file';
import { useLocalization, Skeleton, Image, cn, createFileUrl } from '@magicdoorinc/ui';
import { createSignal, Show } from 'solid-js';
import { DeactivatedFlag } from '~/components/common';
import DisplayAddress from '~/components/common/DisplayAddress';
import { pickProps, TitleWithFeedbackIcon } from '~/components/common/FeedbackIcon/TitleWithFeedbackIcon';
import { getUnitFullname } from '~/components/leases/utils';
import { emptyPlaceholder } from '~/utils/constant';
import { getPublicFileURL } from '~/utils/prefix';
import type { Component } from 'solid-js';
import type { TitleWithFeedbackIconProps } from '~/components/common/FeedbackIcon/TitleWithFeedbackIcon';
import type { HydratedPropertyDto } from '~/swagger/maintenance/Api';
import type { HydratedUnitDto, PropertyDto, UnitDto } from '~/swagger/portal/Api';

export const UnitImage: Component<{
  class?: string;
  unit?: { id?: string; imageId?: string | null };
}> = (props) => {
  const [isLoading, setIsLoading] = createSignal(true);
  const imageUrl = createFileUrl(
    () => (props.unit?.imageId ? new PublicUrlAdapter({ reference: props.unit.imageId }) : undefined),
    () => FileUrlVariant.Thumb
  );

  return (
    <Show when={props.unit?.id} fallback={<Skeleton class={cn('size-11', props.class)} data-slot="image" />}>
      <Image
        class={cn('object-cover', isLoading() ? 'skeleton-shimmer' : 'bg-primary/20', props.class)}
        src={imageUrl()?.url ?? getPublicFileURL('/img/unit-thumb.webp')}
        onLoad={() => setIsLoading(false)}
        fallback={getPublicFileURL('/img/unit-thumb.webp')}
      />
    </Show>
  );
};

export const UnitTitle: Component<
  {
    class?: string;
    unit?: (HydratedUnitDto | UnitDto) & { singleLineAddress?: string };
    property?: PropertyDto | HydratedPropertyDto;
  } & Partial<TitleWithFeedbackIconProps>
> = (props) => {
  const { t } = useLocalization();
  return (
    <TitleWithFeedbackIcon {...pickProps(props)} type="units" entityId={props.unit?.id} initalSelectedIcons={props.unit?.icons}>
      <div class={cn('flex items-center gap-2 text-sm text-text-02', props.class)}>
        <UnitImage class="size-[42px] shrink-0 rounded-md" unit={props.unit} data-slot="image" />
        <div class="flex flex-col">
          <div class="min-w-0 flex-1 space-y-0.5 break-words text-left text-xs font-medium lg:text-sm" data-slot="content">
            <h4 class="break-words" data-slot="title">
              {getUnitFullname({
                property: props.property,
                ...props.unit,
              } as HydratedUnitDto)}
              <DeactivatedFlag class="ml-2" active={props.unit?.active} />
            </h4>
            <p class="text-xs font-normal text-text-03" data-slot="subtitle">
              <Show when={props.unit?.beds}>{t('Beds', { beds: String(props.unit?.beds) })}</Show>
              <Show when={props.unit?.beds && props.unit?.baths}> | </Show>
              <Show when={props.unit?.baths}>{t('Baths', { baths: String(props.unit?.baths) })}</Show>
              <Show when={props.unit?.baths && props.unit?.unitSizeSqft}> | </Show>
              <Show when={props.unit?.unitSizeSqft}>{t('{sqft} Sqft', { sqft: String(props.unit?.unitSizeSqft) })}</Show>
              <Show when={!props.unit?.beds && props.unit?.baths && props.unit?.unitSizeSqft}>{emptyPlaceholder}</Show>
            </p>
          </div>
          <Show when={props.unit?.address}>
            <DisplayAddress class="text-xs text-text-03" address={props.unit?.address} singleLineAddress={props.unit?.singleLineAddress} />
          </Show>
        </div>
      </div>
    </TitleWithFeedbackIcon>
  );
};
